/* Shared surface primitives.
   <Panel> is the section card. <Widget> is the enforcement point for the
   non-negotiables: it takes a ContractQuery and renders WidgetBody in the right
   one of the four states (loading / empty / error+retry / live), and shows a
   "connect me" badge whenever the data is placeholder (an unconnected
   integration). Every data widget on every surface goes through this. */
import type { CSSProperties, ReactNode } from 'react';
import { WidgetBody } from '../ds/mc-kit';
import { Badge } from '../ds';
import type { ContractQuery } from '../lib/api';
import type { ContractKey } from '../lib/contract';

export function Kicker({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', ...style }}>
      {children}
    </span>
  );
}

interface PanelProps {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
  accent?: boolean;
  label?: string;
}

export function Panel({ title, aside, children, style, accent, label }: PanelProps) {
  return (
    <section
      aria-label={label}
      style={{
        background: 'var(--surface)',
        border: `1px solid ${accent ? 'var(--accent-line)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        boxShadow: accent ? '0 18px 50px -32px var(--glow-cyan)' : undefined,
        padding: 18,
        minWidth: 0,
        ...style,
      }}
    >
      {(title || aside) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          {typeof title === 'string' ? <Kicker>{title}</Kicker> : title}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

interface WidgetProps<K extends ContractKey> {
  query: ContractQuery<K>;
  title?: ReactNode;
  aside?: ReactNode;
  minHeight?: number;
  rows?: number;
  emptyLabel?: string;
  emptySub?: string;
  errorLabel?: string;
  errorSub?: string;
  accent?: boolean;
  label?: string;
  /** Hint shown under the widget when the data is placeholder. */
  connect?: string;
  /** Badge label when placeholder: "connect me" for wire-now, "coming soon" for gated. */
  badge?: string;
  children: (data: NonNullable<ContractQuery<K>['data']>) => ReactNode;
}

export function Widget<K extends ContractKey>({
  query,
  title,
  aside,
  minHeight = 120,
  rows = 3,
  emptyLabel,
  emptySub,
  errorLabel,
  errorSub,
  accent,
  label,
  connect,
  badge = 'coming soon',
  children,
}: WidgetProps<K>) {
  const showConnect = query.isPlaceholder && !!connect;
  const asideNode = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {showConnect && <Badge tone="warn">{badge}</Badge>}
      {aside}
    </div>
  );

  return (
    <Panel title={title} aside={title || aside || showConnect ? asideNode : undefined} accent={accent} label={label}>
      <WidgetBody
        state={query.state}
        rows={rows}
        minHeight={minHeight}
        emptyLabel={emptyLabel}
        emptySub={emptySub}
        errorLabel={errorLabel}
        errorSub={errorSub}
        onRetry={query.refetch}
      >
        {query.data != null && children(query.data as NonNullable<ContractQuery<K>['data']>)}
      </WidgetBody>
      {showConnect && (
        <div style={{ marginTop: 10, fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
          {connect} · showing sample data
        </div>
      )}
    </Panel>
  );
}
